import type { ResolvedForm, SubmitResult } from "./types";

/**
 * The token in `/f/<token>`.
 *
 * The Worker serves the same index.html for every path (SPA fallback),
 * exactly as apps/catalog does for `/c/<slug>`, so the route is parsed here
 * rather than by a router. The character class matches base64url plus the dot
 * separating nonce from signature; anything else is not a token this server
 * minted and is rejected without a round trip.
 */
export function formToken(pathname: string): string | null {
  const match = pathname.match(/^\/f\/([A-Za-z0-9._-]+)\/?$/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Where the Convex HTTP endpoints live.
 *
 * The build-time variable is an override, not a requirement — the production
 * origin is baked in, exactly as apps/catalog/src/data.ts does for every
 * non-secret value it needs. A Convex site URL is public by definition.
 *
 * This used to throw when the variable was missing, and that is precisely how
 * the first real form link "expired": the site was built with no variables
 * attached, so the throw happened on load and every link rendered as dead.
 * Depending on deploy-time configuration for something knowable at author time
 * bought nothing and broke the page.
 */
const CONVEX_SITE_URL = (
  // `import.meta.env` is injected by Vite and absent under plain Node, which is
  // how tests/form.test.ts runs — so this has to be optional, not just its value.
  (import.meta.env?.VITE_CONVEX_SITE_URL as string | undefined) ??
  "https://jovial-bison-730.convex.site"
).replace(/\/+$/, "");

/**
 * What the page learned about a token.
 *
 * `closed` and `error` must stay distinct. A `closed` answer is the server
 * saying no, and its copy is deliberately vague so a dead link cannot be told
 * apart from an invented one. An `error` never reached the server at all, and
 * showing that as "this link is no longer active" is a lie to the customer and
 * a dead end for whoever debugs it.
 */
export type ResolveOutcome =
  | { status: "ok"; form: ResolvedForm }
  | { status: "closed" }
  | { status: "error" };

export async function resolveForm(token: string): Promise<ResolveOutcome> {
  let response: Response;
  try {
    response = await fetch(
      `${CONVEX_SITE_URL}/form/resolve?token=${encodeURIComponent(token)}`,
      { method: "GET", headers: { Accept: "application/json" } },
    );
  } catch {
    return { status: "error" };
  }
  // A 5xx is our fault, not a verdict on the link.
  if (!response.ok) return { status: "error" };

  try {
    const form = (await response.json()) as ResolvedForm;
    return form.ok ? { status: "ok", form } : { status: "closed" };
  } catch {
    return { status: "error" };
  }
}

/**
 * One press of Submit, identified so a retry after a lost response returns the
 * saved receipt instead of spending an edit (WID-11).
 *
 * The id is kept while the same answers are retried and dropped once the
 * server gives a verdict (`settle`). Changed answers are a new attempt, so a
 * retry can never resend the old receipt for different content.
 */
export function createSubmitAttempts(
  mint: () => string = () => globalThis.crypto.randomUUID(),
) {
  let pending: { key: string; id: string } | null = null;
  return {
    idFor(answers: Record<string, unknown>): string {
      const key = JSON.stringify(answers);
      if (!pending || pending.key !== key) {
        pending = { key, id: `attempt-${mint()}` };
      }
      return pending.id;
    },
    settle() {
      pending = null;
    },
  };
}

export async function submitForm(
  token: string,
  answers: Record<string, unknown>,
  attempt: { attemptId?: string; baseRevision?: number } = {},
): Promise<SubmitResult> {
  try {
    const response = await fetch(`${CONVEX_SITE_URL}/form/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        answers,
        ...(attempt.attemptId ? { attemptId: attempt.attemptId } : {}),
        ...(typeof attempt.baseRevision === "number"
          ? { baseRevision: attempt.baseRevision }
          : {}),
      }),
    });
    if (!response.ok) {
      return { ok: false, code: "NETWORK", errors: [], successMessage: null };
    }
    return (await response.json()) as SubmitResult;
  } catch {
    // The answers are still in the form; the customer can press submit again,
    // and the same attempt id makes that retry safe.
    return { ok: false, code: "NETWORK", errors: [], successMessage: null };
  }
}
